/*
 * General-purpose utility functions
 */

import { applyPasswordReset, getAuthHeader, sessionState } from "./session";

/*
 * Date / time formatting functions.
 *
 * Will use the user's specified timezone from preferences, or fall back to US East-coast time if unavailable
 */
import moment from "moment-timezone"

export const formatDate = (d: number|Date): string => {
    return toPreferredTimezone(d).format("MMMM D, YYYY");
}

export const formatDateAs = (d: number|Date, format: string): string => {
    return toPreferredTimezone(d).format(format);
}

export const formatDateTime = (d: number|Date): string => {
    return toPreferredTimezone(d).format("MMMM D, YYYY HH:mm");
}

export const formatTime = (d: Date): string => {
    return toPreferredTimezone(d).format("HH:mm");
}

function toPreferredTimezone(d: Date|number) {
    const preferredTZ = sessionState.session?.userPreferences.timezone;
    if (preferredTZ) {
        return moment.utc(d).tz(preferredTZ)
    } else {
        return moment.utc(d).local();
    }
}

export function fromPreferredTimezoneToUTC(s: string, format?: string) {
    const preferredTZ = sessionState.session?.userPreferences.timezone;
    if (preferredTZ) {
        return format ? moment.tz(s, format, preferredTZ).utc().valueOf()
                      : moment.tz(s, preferredTZ).utc().valueOf();
    } else {
        return moment(s, format).utc().valueOf();
    }
}

export const timestampToYYYYMMDD = (ts: number) => {
    return new Date(ts).toISOString().slice(0, 10);
}

export const YYYYMMDDToTimestamp = (s: string) => {
    return new Date(s).valueOf();
}

export const formatAge = (d: number|Date|string): string => {
    if (!(d instanceof Date)) {
        d = new Date(d);
    }
    const today = new Date();

    let age = today.getFullYear() - d.getFullYear();

    const hadBirthdayThisYear = today.getMonth() > d.getMonth()  || (today.getMonth() == d.getMonth() && today.getDate() >= d.getDate());
    if (!hadBirthdayThisYear) { --age; }

    age = Math.max(0, age);

    return age + (age == 1 ? ' year' : ' years');
}

// Checks that a string value includes every term within a supplied set. Ignores case.
export const matchesTerms = (val: string, terms: string[]) => {
    if (terms.length == 0) return true;

    const valLC = val.toLowerCase();

    for (const term of terms) {
        if (!valLC.includes(term.toLowerCase())) return false;
    }

    return true;
};

export const formatName = (first: string, middle: string|undefined|null, last: string) => {
    return middle && middle.length > 0 ? `${first} ${middle} ${last}` : `${first} ${last}`;
}
/*
 * Error / notification toast popups.
 * These will automatically inject themselves into the DOM, so can be used without any template changes.
 */
import { toast } from "bulma-toast";

export const notifyError = (error: string, duration?: number) => {
    if (error.length > 512) { error = error.substring(0,512)+"..."; }
    toast({ message: error, type: 'is-info', position: 'bottom-center', dismissible: true, extraClasses: 'bulmaNotification', duration: duration || 5000 });
}

export const notifyNetworkError = (msg: string, err?: any) => {
    console.error(err);
    notifyError(err ? `${msg} - ${mapHttpError(err).message}` : msg);
}

export const notifySuccess = (message: string) => {
    toast({ message: message, type: 'is-success', position: 'bottom-center', extraClasses: 'bulmaNotification' });
}

export const mapHttpError = (err : any) => {
    console.log("HTTP error", err);
    switch (err.code as string) {
        case "ERR_BAD_REQUEST": {
            switch (err.response.status) {
                case 400: return new Error(`${err.response.data}`);
                case 401: return new Error("You do not have permission to access this");
                case 404: return new Error("Not found - this may have been deleted");
                case 429: return new Error("You are interacting too quickly - please slow down!");
                case 500: return new Error("Server error - please try again, or report this error to Mini City");
            }
            break;
        }
        case "ERR_NETWORK": {
            return new Error("Network error - please check your internet connection and try again");        
        }
    }

    return new Error("Network or server error - please check your internet connection and try again");
}

/*
    Used for removing an item from a TypeScript list
*/
export const removeItemFromList = (list: any[] | undefined, item: any) => {
    console.log("Removing", item, " From ", list);
    if (list == undefined) return
    if (list.length == 1) {
        list.pop(); return list;
    }
    const idx: number | undefined = list.indexOf(item);
    if (idx > -1) {
        list.splice(idx, 1);
    }
    return list;
}

export const deepClone = (obj: any) => {
    return JSON.parse(JSON.stringify(obj));
}

export const generateRandomId = () => {
    const unambiguousChars = "abcdefhjkmnprstwxyz23456789";
    let result = "";
    while (result.length < 16) {
        result = result + unambiguousChars[Math.floor(Math.random()*unambiguousChars.length)];
    }
    return result;
}

export const trimString = (s?: string) => {
    if (!s) return "";
    return s.trim();
}

// This is like Partial<T>, but also makes properties of all nested objects optional
export type DeepPartial<T> = T extends object ? {
    [P in keyof T]?: DeepPartial<T[P]>;
} : T;
